/**
 * BackorderCustomerQuoteTab
 * -------------------------
 * Admin quote-builder embedded inside the AdminBackordersBoard drawer.
 *
 * Flow:
 *   1. Loads the saved draft + auto-suggest (cheapest supplier price per item).
 *   2. Admin edits qty / unit price / lead time / notes per line, or hits
 *      "Auto-fill from supplier quotes (+ markup %)" to populate prices.
 *   3. 20% freight by default; toggle "Hospital pays own freight" → freight $0.
 *   4. Save → Preview → Email customer (Resend if domain verified, otherwise
 *      mailto: opens Outlook). Optional "Attach PDF" checkbox.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Plus, Trash2, Save, Eye, Mail, Download,
  FileText, AlertCircle, CheckCircle2, Lock, DollarSign, Users, UploadCloud, FileSpreadsheet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/App";
import CrmContactPicker from "@/components/admin/CrmContactPicker";

const API = process.env.REACT_APP_BACKEND_URL;

// Roles that can see internal P&L (supplier cost, margin, commission).
// Anyone outside this set sees the same Build Quote tab WITHOUT the cost
// columns or the P&L summary card — sales reps will get a dedicated thin
// view from their own portal that shows only their own commission.
const FINANCIAL_VIEW_ROLES = new Set(["super_admin", "admin_accountant"]);

const emptyLine = () => ({ name: "", sku: "", quantity: 1, uom: "Each", unit_price: 0, lead_time: "", notes: "", supplier_cost: null, substitute: null });

export default function BackorderCustomerQuoteTab({ backorder, token, onSent, prefillFromQuote = null, onPrefillConsumed }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { user } = useAuth();
  const canSeeFinancials = FINANCIAL_VIEW_ROLES.has(user?.role);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Draft state
  const [lineItems, setLineItems] = useState([]);
  const [markupPercent, setMarkupPercent] = useState(30);
  const [freightPercent, setFreightPercent] = useState(20);
  const [useHospitalCourier, setUseHospitalCourier] = useState(false);
  const [freightOverride, setFreightOverride] = useState("");
  const [terms, setTerms] = useState("Net 30");
  const [poInstructions, setPoInstructions] = useState(
    "To place an order, email your PO to orders@sterilemate.com referencing this quote number, " +
    "or reply to this email with approval and we'll cut the PO immediately."
  );
  const [validUntil, setValidUntil] = useState("");
  const [autoSuggest, setAutoSuggest] = useState(null);
  const [quoteNumber, setQuoteNumber] = useState("");
  const [lastSent, setLastSent] = useState(null);

  // P&L state (super_admin + admin_accountant only)
  const [salesReps, setSalesReps] = useState([]);
  const [salesRepId, setSalesRepId] = useState("");
  const [commissionPct, setCommissionPct] = useState(15);
  const [showFinancials, setShowFinancials] = useState(false);

  // Load sales reps once (only matters for financial-view roles)
  useEffect(() => {
    if (!canSeeFinancials) return;
    axios.get(`${API}/api/admin/sales-reps`, { headers })
      .then((r) => setSalesReps(r.data?.sales_reps || r.data || []))
      .catch(() => setSalesReps([]));
  }, [canSeeFinancials, headers]);

  // Send modal state
  const [showSend, setShowSend] = useState(false);
  const [ccList, setCcList] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);

  // Editable recipient — defaults to the backorder's primary contact but the
  // admin can pick anyone from the CRM (or type a one-off address).
  const [toEmail, setToEmail] = useState(backorder.contact_email || "");
  const [toName, setToName] = useState(backorder.contact_name || "");
  const [savePrimary, setSavePrimary] = useState(false);
  const [crmPicker, setCrmPicker] = useState(null); // "to" | "cc" | null

  useEffect(() => {
    setToEmail(backorder.contact_email || "");
    setToName(backorder.contact_name || "");
    setSavePrimary(false);
  }, [backorder.id, backorder.contact_email, backorder.contact_name]);

  const applyCrmPick = (contacts) => {
    const list = (contacts || []).filter((c) => (c.email || "").trim());
    if (!list.length) { setCrmPicker(null); return; }
    if (crmPicker === "cc") {
      const existing = ccList.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
      const merged = Array.from(new Set([...existing, ...list.map((c) => c.email.trim())]));
      setCcList(merged.join(", "));
      toast.success(`${list.length} contact${list.length === 1 ? "" : "s"} added to CC`);
    } else {
      const c = list[0];
      setToEmail(c.email.trim());
      setToName(c.name || "");
      toast.success(`Quote recipient set to ${c.email.trim()}`);
    }
    setCrmPicker(null);
  };

  // ------------------------------------------------------------------ QBO estimate intake
  // Second way to fill this tab: upload the QuickBooks estimate PDF and let
  // AskIsaac key the lines. Nothing lands on the draft until "Apply".
  const estInputRef = useRef(null);
  const [estBusy, setEstBusy] = useState(false);
  const [estProposal, setEstProposal] = useState(null);     // parsed proposal under review
  const [estPicked, setEstPicked] = useState([]);           // bool per parsed line
  const [estFreightToOverride, setEstFreightToOverride] = useState(true);
  const [qboEstimate, setQboEstimate] = useState({ number: "", date: "" });

  const uploadEstimate = async (file) => {
    if (!file) return;
    setEstBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/customer-quote/parse-estimate`,
        fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      const p = r.data?.proposal;
      if (!p || !(p.line_items || []).length) {
        toast.error("No line items could be read off that estimate");
        return;
      }
      setEstProposal(p);
      setEstPicked((p.line_items || []).map(() => true));
      setEstFreightToOverride((p.freight_total || 0) > 0);
      toast.success(`Estimate ${p.estimate_number || ""} read — ${p.line_items.length} line${p.line_items.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read that estimate");
    } finally {
      setEstBusy(false);
      if (estInputRef.current) estInputRef.current.value = "";
    }
  };

  const applyEstimate = (mode) => {
    const p = estProposal;
    if (!p) return;
    const picked = (p.line_items || []).filter((_, i) => estPicked[i]);
    const freightLines = picked.filter((li) => li.is_freight && estFreightToOverride);
    const productLines = picked.filter((li) => !(li.is_freight && estFreightToOverride));

    const mapped = productLines.map((li) => ({
      ...emptyLine(),
      name: li.name || "",
      sku: li.sku || "",
      quantity: parseInt(li.quantity) || 1,
      uom: li.uom || "Each",
      unit_price: parseFloat(li.unit_price) || 0,
      notes: li.notes || "",
    }));
    if (!mapped.length && !freightLines.length) {
      toast.error("Tick at least one line to apply");
      return;
    }
    setLineItems(mode === "append" ? [...lineItems, ...mapped] : (mapped.length ? mapped : lineItems));

    if (freightLines.length) {
      const freight = freightLines.reduce(
        (s, li) => s + (parseFloat(li.extended_price) || 0), 0);
      setFreightOverride(Math.round(freight * 100) / 100);
      setUseHospitalCourier(false);
    }
    if (p.terms) setTerms(p.terms);
    if (p.expiration_date) setValidUntil(p.expiration_date);
    if (p.estimate_number && !quoteNumber) setQuoteNumber(`Q-${p.estimate_number}`);
    setQboEstimate({ number: p.estimate_number || "", date: p.estimate_date || "" });
    setEstProposal(null);
    toast.success(
      `${mapped.length} line${mapped.length === 1 ? "" : "s"} ${mode === "append" ? "appended" : "applied"}`
      + (freightLines.length ? ` · freight set to $${freightLines.reduce((s, li) => s + (parseFloat(li.extended_price) || 0), 0).toFixed(2)}` : "")
      + " — press Save draft to keep it."
    );
  };

  // Load draft + suggestions
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/admin/backorders/${backorder.id}/customer-quote`, { headers });
        if (!alive) return;
        const d = r.data || {};
        const draft = d.draft;
        setAutoSuggest(d.auto_suggest);
        setQuoteNumber(draft?.quote_number || "");
        setLastSent(draft?.last_sent_at ? { at: draft.last_sent_at, to: draft.last_sent_to, status: draft.last_send_status } : null);

        if (draft && (draft.line_items || []).length > 0) {
          setLineItems(draft.line_items);
          setMarkupPercent(draft.markup_percent ?? 30);
          setFreightPercent(draft.freight_percent ?? 20);
          setUseHospitalCourier(!!draft.use_hospital_courier);
          setFreightOverride(draft.freight_override ?? "");
          setTerms(draft.terms || "Net 30");
          setPoInstructions(draft.po_instructions || poInstructions);
          setValidUntil(draft.valid_until || "");
          setSalesRepId(draft.sales_rep_id || "");
          setCommissionPct(draft.commission_pct ?? 15);
          setQboEstimate({ number: draft.qbo_estimate_number || "", date: draft.qbo_estimate_date || "" });
        } else {
          // Pre-fill from the backorder items list as a starting point
          const items = (d.backorder?.items || []).map((it) => ({
            name: it.name || it.item_name || "",
            sku: it.sku || it.mfr_number || "",
            quantity: parseInt(it.quantity) || 1,
            uom: it.uom || it.quantity_type || "Each",
            unit_price: 0,
            lead_time: "",
            notes: it.notes || "",
            substitute: it.substitute || null,
          }));
          setLineItems(items.length ? items : [emptyLine()]);
          setUseHospitalCourier(!!d.backorder?.use_hospital_courier);
        }
      } catch {
        toast.error("Failed to load quote draft");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backorder.id]);

  // Live totals
  const totals = useMemo(() => {
    const subtotal = lineItems.reduce(
      (s, it) => s + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0), 0
    );
    let freight = 0;
    if (useHospitalCourier) freight = 0;
    else if (freightOverride !== "" && !isNaN(parseFloat(freightOverride))) freight = parseFloat(freightOverride);
    else freight = subtotal * (parseFloat(freightPercent) || 0) / 100;
    // Internal: total supplier cost (sum of supplier_cost × qty across lines).
    // Lines with null supplier_cost contribute 0 — caller is warned by the UI.
    const supplierCostTotal = lineItems.reduce(
      (s, it) => s + (parseFloat(it.supplier_cost) || 0) * (parseInt(it.quantity) || 0), 0,
    );
    const grossMargin = subtotal - supplierCostTotal;
    const marginPct = subtotal > 0 ? (grossMargin / subtotal) * 100 : 0;
    const commission = grossMargin * (parseFloat(commissionPct) || 0) / 100;
    const netToCompany = grossMargin - commission;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      freight: Math.round(freight * 100) / 100,
      total: Math.round((subtotal + freight) * 100) / 100,
      supplier_cost_total: Math.round(supplierCostTotal * 100) / 100,
      gross_margin: Math.round(grossMargin * 100) / 100,
      margin_pct: Math.round(marginPct * 10) / 10,
      commission: Math.round(commission * 100) / 100,
      net_to_company: Math.round(netToCompany * 100) / 100,
    };
  }, [lineItems, freightPercent, useHospitalCourier, freightOverride, commissionPct]);

  // Auto-fill prices from awarded/cheapest supplier quote × markup. Used by
  // the "Apply markup" button when the admin wants to PULL FRESH supplier
  // costs from the auto-suggest payload AND apply the markup in one shot.
  //
  // Behavior (backend chooses per-line):
  //   • If a supplier has been AWARDED for a specific line, that supplier's
  //     price (or the price captured on the award itself) is used.
  //   • Otherwise the cheapest supplier quote for that item is used.
  // Either way, the backend already applies the markup and returns
  // `unit_price` ready to drop into the hospital quote — but we recompute
  // client-side too so the admin can slide the markup % without a round-trip.
  const applyAutoSuggest = () => {
    if (!autoSuggest || !autoSuggest.line_items?.length) {
      toast.info("No supplier quotes attached to this backorder yet. Use 'Source from Suppliers' first.");
      return;
    }
    const m = (parseFloat(markupPercent) || 0) / 100;
    const next = autoSuggest.line_items.map((s) => {
      const matched = lineItems.find(
        (li) =>
          (li.line_id && s.line_id && li.line_id === s.line_id) ||
          (li.name || "").trim().toLowerCase() === (s.name || "").trim().toLowerCase()
      );
      const supplierCost = s.supplier_cost;
      // Prefer the freshly computed markup so slider changes reflect immediately
      const suggested = supplierCost != null
        ? Math.round(supplierCost * (1 + m) * 100) / 100
        : (s.unit_price ?? matched?.unit_price ?? 0);
      return {
        name: s.name,
        sku: s.sku || matched?.sku || "",
        description: s.description || matched?.description || "",
        quantity: matched?.quantity ?? s.quantity ?? 1,
        uom: s.uom || matched?.uom || "Each",
        unit_price: suggested,
        lead_time: matched?.lead_time || s.lead_time || "",
        notes: matched?.notes ?? s.notes ?? "",
        supplier_cost: supplierCost,
        awarded_supplier: s.awarded_supplier || null,
        source: s.source || (supplierCost != null ? "cheapest" : null),
        line_id: s.line_id || matched?.line_id || null,
        substitute: matched?.substitute || null,
      };
    });
    setLineItems(next);
    const priced = next.filter((n) => n.unit_price > 0).length;
    const awarded = next.filter((n) => n.source === "awarded").length;
    if (awarded > 0) {
      toast.success(
        `Auto-filled ${priced} line${priced === 1 ? "" : "s"} · ${awarded} from awarded suppliers (${markupPercent}% markup)`,
        { description: awarded < priced ? `${priced - awarded} pulled from cheapest quote (no award yet)` : "All lines use their awarded supplier's price." }
      );
    } else {
      toast.success(`Auto-filled ${priced} line${priced === 1 ? "" : "s"} from cheapest supplier quotes (${markupPercent}% markup)`);
    }
  };

  const setItem = (idx, field, val) => {
    setLineItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  };
  const addItem = () => setLineItems((prev) => [...prev, emptyLine()]);
  const removeItem = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));

  // Prefill from an accepted supplier quote (admin clicked "Accept & Build
  // Hospital Quote" on a supplier-quote card). Each item_quotes row gives us
  // a supplier_cost + qty_available + lead_time; we layer the current markup %
  // to produce the customer-facing unit_price.
  useEffect(() => {
    if (!prefillFromQuote || !Array.isArray(prefillFromQuote.item_quotes) || prefillFromQuote.item_quotes.length === 0) {
      return;
    }
    const m = (parseFloat(markupPercent) || 0) / 100;
    const next = prefillFromQuote.item_quotes.map((it) => {
      const supplierCost = it.unit_price != null ? parseFloat(it.unit_price) : null;
      const retail = supplierCost != null && !Number.isNaN(supplierCost)
        ? Math.round(supplierCost * (1 + m) * 100) / 100
        : 0;
      return {
        name: it.name || "",
        sku: it.sku || "",
        quantity: it.qty_available || it.quantity_requested || 1,
        uom: it.uom || "Each",
        unit_price: retail,
        lead_time: it.lead_time || prefillFromQuote.lead_time || "",
        notes: "",
        supplier_cost: supplierCost,
        substitute: null,
      };
    });
    setLineItems(next);
    const priced = next.filter((n) => n.unit_price > 0).length;
    toast.success(
      `Hospital Quote prefilled from ${prefillFromQuote.supplier_name}`,
      { description: `${priced} of ${next.length} line${next.length === 1 ? "" : "s"} priced at ${markupPercent}% markup. Adjust as needed.` },
    );
    onPrefillConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillFromQuote?.id]);

  // Helpers — keep cost ↔ markup ↔ retail in sync reactively, so the admin
  // doesn't have to click "Apply markup" after every tweak.
  const computeRetail = (cost, markup) => {
    const c = parseFloat(cost);
    const m = parseFloat(markup);
    if (Number.isNaN(c) || Number.isNaN(m) || c <= 0) return null;
    return Math.round(c * (1 + m / 100) * 100) / 100;
  };
  const onMarkupChange = (val) => {
    setMarkupPercent(val);
    setLineItems((prev) => prev.map((it) => {
      const next = computeRetail(it.supplier_cost, val);
      return next == null ? it : { ...it, unit_price: next };
    }));
  };
  const onSupplierCostChange = (idx, v) => {
    setLineItems((prev) => prev.map((li, i) => {
      if (i !== idx) return li;
      const cost = v === "" ? null : parseFloat(v);
      const next = computeRetail(cost, markupPercent);
      return next == null
        ? { ...li, supplier_cost: cost }
        : { ...li, supplier_cost: cost, unit_price: next };
    }));
  };

  // Save draft
  const save = async () => {
    const valid = lineItems.filter((it) => (it.name || "").trim());
    if (valid.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        line_items: valid.map((it) => ({
          name: it.name,
          sku: (it.sku || "").trim() || null,
          quantity: parseInt(it.quantity) || 1,
          uom: it.uom || "Each",
          unit_price: parseFloat(it.unit_price) || 0,
          lead_time: it.lead_time || null,
          notes: it.notes || null,
          supplier_cost: it.supplier_cost ?? null,
          substitute: it.substitute || null,
        })),
        markup_percent: parseFloat(markupPercent) || 0,
        freight_percent: parseFloat(freightPercent) || 0,
        use_hospital_courier: useHospitalCourier,
        freight_override: freightOverride === "" ? null : parseFloat(freightOverride),
        terms,
        po_instructions: poInstructions,
        valid_until: validUntil || null,
        quote_number: quoteNumber || null,
        qbo_estimate_number: qboEstimate.number || null,
        qbo_estimate_date: qboEstimate.date || null,
        sales_rep_id: salesRepId || null,
        commission_pct: parseFloat(commissionPct) || 0,
      };
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/customer-quote`, payload, { headers }
      );
      setQuoteNumber(r.data?.draft?.quote_number || quoteNumber);
      toast.success("Quote draft saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    try {
      await save();
      const r = await axios.get(
        `${API}/api/admin/backorders/${backorder.id}/customer-quote/pdf`,
        { headers, responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `SterileMate-Quote-${quoteNumber || backorder.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    }
  };

  const previewPdf = async () => {
    try {
      await save();
      const r = await axios.get(
        `${API}/api/admin/backorders/${backorder.id}/customer-quote/pdf`,
        { headers, responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("Preview failed");
    }
  };

  const send = async () => {
    setSending(true);
    try {
      await save();
      const cc = ccList.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/customer-quote/send`,
        {
          cc,
          attach_pdf: attachPdf,
          custom_message: customMessage || null,
          to_email: (toEmail || "").trim() || null,
          to_name: (toName || "").trim() || null,
          save_as_primary: savePrimary,
        },
        { headers }
      );
      const d = r.data || {};
      if (d.ok) {
        toast.success(`Quote sent to ${d.to}${cc.length ? ` (cc ${cc.length})` : ""}`);
        setLastSent({ at: new Date().toISOString(), to: d.to, status: "success" });
        onSent?.();
      } else {
        // SMTP unavailable — open mailto fallback
        toast.warning("SMTP unavailable — opening Outlook draft. (Verify Resend domain to enable direct send.)");
        if (d.mailto_fallback) window.location.href = d.mailto_fallback;
        setLastSent({ at: new Date().toISOString(), to: d.to, status: "mailto_fallback" });
      }
      setShowSend(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading quote…</div>;
  }

  return (
    <div className="space-y-5" data-testid="bo-customer-quote-tab">
      {/* Summary banner */}
      <div className="rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-3 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Quote Number</div>
          <div className="text-sm font-mono font-bold text-slate-900">{quoteNumber || `Q-${backorder.id?.slice(-8)?.toUpperCase()}`}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Customer</div>
          <div className="text-sm font-bold text-slate-900 truncate">{backorder.hospital_name || "—"}</div>
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="text-[11px] text-slate-500 truncate" data-testid="quote-to-email-label">
              {toEmail || "no email on file"}
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-5 px-1.5 text-[10px] font-bold text-amber-800 hover:bg-amber-50 shrink-0"
              onClick={() => setCrmPicker("to")}
              data-testid="quote-change-recipient-btn"
            >
              <Users className="w-3 h-3 mr-1" /> Change
            </Button>
          </div>
        </div>
        {lastSent && (
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Last Sent</div>
            <div className={`text-xs font-semibold ${lastSent.status === "success" ? "text-emerald-700" : "text-amber-700"}`}>
              {lastSent.status === "success" ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
              {new Date(lastSent.at).toLocaleString()} → {lastSent.to}
            </div>
          </div>
        )}
        <Button
          size="sm"
          onClick={() => setShowSend(true)}
          disabled={!toEmail || lineItems.length === 0}
          className="gap-1.5 bg-amber-600 hover:bg-amber-700"
          data-testid="quote-send-top-btn"
        >
          <Mail className="w-3.5 h-3.5" /> Email customer
        </Button>
      </div>

      {/* Auto-fill controls */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs font-bold text-amber-900 flex items-center gap-1.5 mb-0.5">
              <Sparkles className="w-3.5 h-3.5" /> Auto-fill from supplier quotes
            </div>
            <div className="text-[11px] text-amber-800/80">
              {autoSuggest?.lines_covered_by_awards > 0 ? (
                <>
                  <span className="font-bold text-violet-700">
                    {autoSuggest.lines_covered_by_awards} of {autoSuggest.line_items?.length || 0} line{autoSuggest.line_items?.length === 1 ? "" : "s"} will pull from AWARDED suppliers
                  </span>
                  {autoSuggest.quote_count > 0 && (
                    <span> · remaining lines will use cheapest quote across {autoSuggest.quote_count} supplier{autoSuggest.quote_count === 1 ? "" : "s"}.</span>
                  )}
                </>
              ) : autoSuggest?.quote_count ? (
                `${autoSuggest.quote_count} supplier quote${autoSuggest.quote_count === 1 ? "" : "s"} attached — cheapest per line will be used. Award a supplier on the Awards tab first to lock in specific per-line pricing.`
              ) : (
                "No supplier quotes yet — click 'Source from Suppliers' on the action toolbar to gather pricing first."
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-amber-900 font-semibold">Markup %</label>
            <Input
              type="number"
              value={markupPercent}
              onChange={(e) => onMarkupChange(e.target.value)}
              className="h-8 w-20 text-sm"
              data-testid="quote-markup-percent"
            />
          </div>
          <Button
            size="sm"
            onClick={applyAutoSuggest}
            disabled={!autoSuggest?.quote_count && !autoSuggest?.lines_covered_by_awards}
            className="bg-amber-600 hover:bg-amber-700 gap-1.5"
            data-testid="quote-auto-fill-btn"
          >
            <Sparkles className="w-3.5 h-3.5" /> Apply markup
          </Button>
        </div>
      </div>

      {/* QuickBooks estimate intake — build the quote off an existing QBO
          estimate instead of keying every line by hand. */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs font-bold text-emerald-900 flex items-center gap-1.5 mb-0.5">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Build from a QuickBooks estimate
            </div>
            <div className="text-[11px] text-emerald-900/80">
              {qboEstimate.number
                ? <>Built from QuickBooks estimate <strong>#{qboEstimate.number}</strong>{qboEstimate.date ? ` (${qboEstimate.date})` : ""} — upload a newer PDF to re-read it.</>
                : "Upload the estimate PDF and AskIsaac keys the lines, rates, terms and freight for you. You review every line before it lands."}
            </div>
          </div>
          <input
            ref={estInputRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => uploadEstimate(e.target.files?.[0])}
            data-testid="quote-estimate-file-input"
          />
          <Button
            size="sm"
            onClick={() => estInputRef.current?.click()}
            disabled={estBusy}
            className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"
            data-testid="quote-upload-estimate-btn"
          >
            {estBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
            {estBusy ? "Reading estimate…" : "Upload QuickBooks estimate"}
          </Button>
        </div>
      </div>

      {/* Line items */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Line items</h3>
            {canSeeFinancials ? (
              <p className="text-[11px] text-slate-500 mt-0.5">
                Type the <strong className="text-rose-700">Supplier&nbsp;$</strong> for each line — the <strong className="text-emerald-700">Unit&nbsp;$&nbsp;(Retail)</strong> auto-fills using your current <strong>Markup&nbsp;%</strong>. The customer only ever sees the Retail column.
              </p>
            ) : (
              <p className="text-[11px] text-slate-500 mt-0.5">Fill Item, Qty, UOM, Unit&nbsp;$, Lead Time. Line totals auto-calculate.</p>
            )}
          </div>
          <button onClick={addItem} className="text-xs font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add line
          </button>
        </div>
        <div className="hidden min-[900px]:block overflow-x-auto border border-slate-200 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 text-left" title="Product name as it should appear on the customer's quote">Item / Description</th>
                <th className="px-2 py-2 text-left w-32" title="SKU or product code">SKU</th>
                <th className="px-2 py-2 text-center w-20" title="Quantity to quote the hospital">Qty</th>
                <th className="px-2 py-2 text-center w-24" title="Unit of measure (Each, Case, Box, etc.)">UOM</th>
                {canSeeFinancials && (
                  <th className="px-2 py-2 text-right w-28 bg-rose-50 text-rose-700" title="INTERNAL — supplier cost per unit. Never sent to customer. Drives Unit $ via current Markup %.">
                    Supplier&nbsp;$&nbsp;🔒
                  </th>
                )}
                <th className="px-2 py-2 text-right w-28" title="Unit price the customer will see on the quote (USD)">
                  Unit&nbsp;$&nbsp;(Retail)
                </th>
                <th className="px-2 py-2 text-left w-32" title="Lead time / ETA to deliver to the hospital">Lead Time</th>
                <th className="px-2 py-2 text-left" title="Per-line note shown on the customer-facing quote">Notes</th>
                <th className="px-2 py-2 text-right w-28" title="Auto: Qty × Unit $">Line Total</th>
                <th className="px-2 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lineItems.map((it, idx) => {
                const lineTotal = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0);
                const sub = it.substitute;
                const subLineTotal = sub ? (parseFloat(sub.unit_price) || 0) * (parseInt(sub.quantity || it.quantity) || 0) : 0;
                return (
                  <Fragment key={idx}>
                    <tr className="hover:bg-amber-50/40">
                      <td className="px-2 py-1">
                        <Input value={it.name} onChange={(e) => setItem(idx, "name", e.target.value)} className="h-8 text-sm" placeholder="Item name *" data-testid={`quote-line-name-${idx}`} />
                      </td>
                      <td className="px-2 py-1">
                        <Input value={it.sku || ""} onChange={(e) => setItem(idx, "sku", e.target.value)} className="h-8 text-xs font-mono" placeholder="SKU" data-testid={`quote-line-sku-${idx}`} />
                      </td>
                      <td className="px-2 py-1">
                        <Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="h-8 text-sm text-center" data-testid={`quote-line-qty-${idx}`} />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={it.uom || "Each"}
                          onChange={(e) => setItem(idx, "uom", e.target.value)}
                          className="h-8 w-full rounded border border-slate-200 bg-white text-xs px-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
                          data-testid={`quote-line-uom-${idx}`}
                          aria-label="Unit of measure"
                        >
                          <option value="Each">Each</option>
                          <option value="Case">Case</option>
                          <option value="Box">Box</option>
                          <option value="Pack">Pack</option>
                          <option value="Pair">Pair</option>
                          <option value="Bag">Bag</option>
                          <option value="Roll">Roll</option>
                          <option value="Bottle">Bottle</option>
                          <option value="Kit">Kit</option>
                          <option value="Pallet">Pallet</option>
                        </select>
                      </td>
                      {canSeeFinancials && (
                        <td className="px-2 py-1 bg-rose-50/40">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={it.supplier_cost ?? ""}
                            onChange={(e) => onSupplierCostChange(idx, e.target.value)}
                            className="h-8 text-sm text-right bg-white border-rose-200 focus:border-rose-400"
                            placeholder="—"
                            title="Internal supplier cost (never sent to customer). Changes here auto-update Unit $ via the current markup %."
                            data-testid={`quote-line-supplier-cost-${idx}`}
                          />
                        </td>
                      )}
                      <td className="px-2 py-1">
                        <Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="h-8 text-sm text-right" data-testid={`quote-line-price-${idx}`} />
                      </td>
                      <td className="px-2 py-1">
                        <Input value={it.lead_time || ""} onChange={(e) => setItem(idx, "lead_time", e.target.value)} className="h-8 text-sm" placeholder="e.g. 5-7d" />
                      </td>
                      <td className="px-2 py-1">
                        <Input value={it.notes || ""} onChange={(e) => setItem(idx, "notes", e.target.value)} className="h-8 text-sm" placeholder="per-line note" />
                      </td>
                      <td className="px-2 py-1 text-right font-semibold text-slate-900">${lineTotal.toFixed(2)}</td>
                      <td className="px-2 py-1 text-right">
                        {lineItems.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="text-slate-400 hover:text-rose-600 p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Substitute row */}
                    {sub ? (
                      <tr className="bg-amber-50/70">
                        <td className="px-2 py-1 pl-6">
                          <div className="text-[9px] font-bold uppercase tracking-wider text-amber-700 mb-0.5">↳ Substitute</div>
                          <Input value={sub.name || ""} onChange={(e) => setItem(idx, "substitute", { ...sub, name: e.target.value })} className="h-8 text-sm bg-white" placeholder="Substitute name" data-testid={`quote-sub-name-${idx}`} />
                        </td>
                        <td className="px-2 py-1">
                          <Input value={sub.sku || ""} onChange={(e) => setItem(idx, "substitute", { ...sub, sku: e.target.value })} className="h-8 text-xs font-mono bg-white" placeholder="Sub SKU" data-testid={`quote-sub-sku-${idx}`} />
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" min="1" value={sub.quantity ?? it.quantity} onChange={(e) => setItem(idx, "substitute", { ...sub, quantity: e.target.value })} className="h-8 text-sm text-center bg-white" />
                        </td>
                        <td className="px-2 py-1 text-[11px] text-amber-700 italic text-center">—</td>
                        {canSeeFinancials && (
                          <td className="px-2 py-1 bg-rose-50/30 text-[11px] text-rose-400 italic text-center">—</td>
                        )}
                        <td className="px-2 py-1">
                          <Input type="number" step="0.01" min="0" value={sub.unit_price ?? ""} onChange={(e) => setItem(idx, "substitute", { ...sub, unit_price: e.target.value })} className="h-8 text-sm text-right bg-white" placeholder="Sub unit $" />
                        </td>
                        <td className="px-2 py-1 text-[11px] text-amber-700 italic">same lead</td>
                        <td className="px-2 py-1">
                          <Input value={sub.notes || ""} onChange={(e) => setItem(idx, "substitute", { ...sub, notes: e.target.value })} className="h-8 text-sm bg-white" placeholder="Why this sub?" />
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-amber-900">${subLineTotal.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right">
                          <button onClick={() => setItem(idx, "substitute", null)} className="text-amber-500 hover:text-rose-600 p-1" title="Remove substitute">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan={canSeeFinancials ? 10 : 9} className="px-2 py-0.5 pl-6">
                          <button
                            onClick={() => setItem(idx, "substitute", { name: "", sku: "", quantity: it.quantity, unit_price: 0, notes: "" })}
                            className="text-[11px] font-semibold text-amber-700 hover:text-amber-800"
                            data-testid={`quote-add-sub-${idx}`}
                          >+ Add substitute option</button>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MOBILE — Stacked card view (< 900px). Same handlers as the table above. */}
        <div className="block min-[900px]:hidden space-y-3">
          {lineItems.map((it, idx) => {
            const lineTotal = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0);
            const sub = it.substitute;
            const subLineTotal = sub ? (parseFloat(sub.unit_price) || 0) * (parseInt(sub.quantity || it.quantity) || 0) : 0;
            return (
              <div key={idx} className="rounded-lg border border-slate-200 bg-white shadow-sm p-3 space-y-2.5" data-testid={`quote-line-mobile-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Line&nbsp;{idx + 1}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-slate-900">${lineTotal.toFixed(2)}</span>
                    {lineItems.length > 1 && (
                      <button onClick={() => removeItem(idx)} className="text-slate-400 hover:text-rose-600 p-1 -mr-1" title="Remove line">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Item / Description</label>
                  <Input value={it.name} onChange={(e) => setItem(idx, "name", e.target.value)} className="h-9 mt-1" placeholder="Item name *" />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">SKU</label>
                  <Input value={it.sku || ""} onChange={(e) => setItem(idx, "sku", e.target.value)} className="h-9 mt-1 font-mono text-xs" placeholder="SKU" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Qty</label>
                    <Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="h-9 mt-1 text-center" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">UOM</label>
                    <select
                      value={it.uom || "Each"}
                      onChange={(e) => setItem(idx, "uom", e.target.value)}
                      className="h-9 mt-1 w-full rounded border border-slate-200 bg-white text-sm px-2"
                    >
                      <option value="Each">Each</option>
                      <option value="Case">Case</option>
                      <option value="Box">Box</option>
                      <option value="Pack">Pack</option>
                      <option value="Pair">Pair</option>
                      <option value="Bag">Bag</option>
                      <option value="Roll">Roll</option>
                      <option value="Bottle">Bottle</option>
                      <option value="Kit">Kit</option>
                      <option value="Pallet">Pallet</option>
                    </select>
                  </div>
                </div>

                <div className={canSeeFinancials ? "grid grid-cols-2 gap-2" : ""}>
                  {canSeeFinancials && (
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-rose-700 flex items-center gap-1">
                        Supplier&nbsp;$&nbsp;🔒
                      </label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={it.supplier_cost ?? ""}
                        onChange={(e) => onSupplierCostChange(idx, e.target.value)}
                        className="h-9 mt-1 text-right bg-rose-50/40 border-rose-200"
                        placeholder="—"
                        title="Internal cost. Drives Unit $ via current Markup %. Never sent to customer."
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Unit&nbsp;$&nbsp;(Retail)</label>
                    <Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="h-9 mt-1 text-right font-semibold" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Lead Time</label>
                  <Input value={it.lead_time || ""} onChange={(e) => setItem(idx, "lead_time", e.target.value)} className="h-9 mt-1" placeholder="e.g. 5-7 business days" />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Notes (shown on customer quote)</label>
                  <Input value={it.notes || ""} onChange={(e) => setItem(idx, "notes", e.target.value)} className="h-9 mt-1" placeholder="per-line note" />
                </div>

                {/* Substitute (mobile) */}
                {sub ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 space-y-2 mt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700">↳ Substitute Option</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-amber-900">${subLineTotal.toFixed(2)}</span>
                        <button onClick={() => setItem(idx, "substitute", null)} className="text-amber-500 hover:text-rose-600 p-1 -mr-1" title="Remove substitute">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <Input value={sub.name || ""} onChange={(e) => setItem(idx, "substitute", { ...sub, name: e.target.value })} className="h-9 bg-white" placeholder="Substitute name" />
                    <div className="grid grid-cols-3 gap-2">
                      <Input value={sub.sku || ""} onChange={(e) => setItem(idx, "substitute", { ...sub, sku: e.target.value })} className="h-9 font-mono text-xs bg-white" placeholder="SKU" />
                      <Input type="number" min="1" value={sub.quantity ?? it.quantity} onChange={(e) => setItem(idx, "substitute", { ...sub, quantity: e.target.value })} className="h-9 text-center bg-white" placeholder="Qty" />
                      <Input type="number" step="0.01" min="0" value={sub.unit_price ?? ""} onChange={(e) => setItem(idx, "substitute", { ...sub, unit_price: e.target.value })} className="h-9 text-right bg-white" placeholder="Unit $" />
                    </div>
                    <Input value={sub.notes || ""} onChange={(e) => setItem(idx, "substitute", { ...sub, notes: e.target.value })} className="h-9 bg-white" placeholder="Why this sub?" />
                  </div>
                ) : (
                  <button
                    onClick={() => setItem(idx, "substitute", { name: "", sku: "", quantity: it.quantity, unit_price: 0, notes: "" })}
                    className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 mt-1"
                  >
                    + Add substitute option
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Freight + Terms */}
      <section className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 p-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Freight</h3>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={useHospitalCourier}
              onChange={(e) => setUseHospitalCourier(e.target.checked)}
              className="accent-amber-600"
              data-testid="quote-hospital-courier"
            />
            Hospital pays own freight (zero freight on quote)
          </label>
          {!useHospitalCourier && (
            <div className="mt-2 grid grid-cols-2 gap-2 items-end">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">% of subtotal</label>
                <Input type="number" value={freightPercent} onChange={(e) => setFreightPercent(e.target.value)} className="h-8 text-sm" data-testid="quote-freight-percent" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Or $ override</label>
                <Input type="number" step="0.01" value={freightOverride} onChange={(e) => setFreightOverride(e.target.value)} className="h-8 text-sm" placeholder="blank = use %" />
              </div>
            </div>
          )}
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Terms & validity</h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Terms</label>
              <Input value={terms} onChange={(e) => setTerms(e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Valid until</label>
              <Input value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="h-8 text-sm" placeholder="e.g. 30 days from issue" />
            </div>
          </div>
        </div>
      </section>

      {/* PO Instructions */}
      <section>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">PO instructions (printed on quote)</h3>
        <Textarea rows={2} value={poInstructions} onChange={(e) => setPoInstructions(e.target.value)} className="text-sm" />
      </section>

      {/* Totals */}
      <section className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
        <div className="grid grid-cols-3 gap-2 text-right">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Subtotal</div>
            <div className="text-lg font-bold text-slate-900">${totals.subtotal.toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
              Freight {useHospitalCourier && "(hospital courier)"}
            </div>
            <div className="text-lg font-bold text-slate-900">${totals.freight.toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Total Due</div>
            <div className="text-2xl font-extrabold text-emerald-700">${totals.total.toFixed(2)}</div>
          </div>
        </div>
      </section>

      {/* ============================================================
          INTERNAL P&L — visible ONLY to super_admin & admin_accountant.
          Sales reps see their commission on their own portal, never
          supplier cost or margin. Hospital NEVER sees any of this.
          ============================================================ */}
      {canSeeFinancials && (
        <section
          className="rounded-lg border-2 border-slate-800 bg-slate-900 text-slate-100 p-4 space-y-3"
          data-testid="internal-pnl-panel"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold uppercase tracking-wider text-amber-300 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Internal P&amp;L — confidential
              <span className="text-[10px] text-slate-400 normal-case font-normal">
                visible to super_admin · admin_accountant
              </span>
            </h3>
            <button
              type="button"
              onClick={() => setShowFinancials((v) => !v)}
              className="text-xs font-semibold text-amber-300 hover:text-amber-200"
              data-testid="internal-pnl-toggle"
            >
              {showFinancials ? "Hide" : "Show"} ▾
            </button>
          </div>

          {showFinancials && (
            <>
              {/* Sales rep + commission selector */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-800 rounded p-3 border border-slate-700">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Sales rep</label>
                  <select
                    value={salesRepId}
                    onChange={(e) => {
                      setSalesRepId(e.target.value);
                      const rep = salesReps.find((r) => r.id === e.target.value);
                      if (rep?.default_commission_pct != null) setCommissionPct(rep.default_commission_pct);
                    }}
                    className="w-full bg-slate-900 text-slate-100 border border-slate-700 rounded px-2 py-1.5 text-sm"
                    data-testid="pnl-sales-rep-select"
                  >
                    <option value="">— None / house deal —</option>
                    {salesReps.filter((r) => r.is_active !== false).map((r) => (
                      <option key={r.id} value={r.id}>{r.name} ({r.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Commission %</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={commissionPct}
                    onChange={(e) => setCommissionPct(e.target.value)}
                    className="bg-slate-900 text-slate-100 border border-slate-700 h-8 text-sm"
                    data-testid="pnl-commission-pct"
                  />
                </div>
                <div className="self-end text-[11px] text-slate-400">
                  Commission is computed on <strong>gross margin</strong>, not gross revenue.
                </div>
              </div>

              {/* Per-line cost breakdown */}
              <div className="overflow-x-auto bg-slate-800 rounded border border-slate-700">
                <table className="w-full text-sm" data-testid="pnl-line-table">
                  <thead className="text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-700">
                    <tr>
                      <th className="px-2 py-2 text-left">Item</th>
                      <th className="px-2 py-2 text-center w-12">Qty</th>
                      <th className="px-2 py-2 text-right w-24">Supplier $</th>
                      <th className="px-2 py-2 text-right w-24">Hospital $</th>
                      <th className="px-2 py-2 text-right w-24">Line margin</th>
                      <th className="px-2 py-2 text-right w-16">%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {lineItems.map((it, idx) => {
                      const qty = parseInt(it.quantity) || 0;
                      const unit = parseFloat(it.unit_price) || 0;
                      const cost = parseFloat(it.supplier_cost);
                      const hasCost = !isNaN(cost) && cost > 0;
                      const lineRev = unit * qty;
                      const lineCost = hasCost ? cost * qty : 0;
                      const lineMargin = hasCost ? lineRev - lineCost : null;
                      const linePct = hasCost && lineRev > 0 ? (lineMargin / lineRev) * 100 : null;
                      return (
                        <tr key={idx}>
                          <td className="px-2 py-1.5 text-slate-200 truncate max-w-[200px]" title={it.name}>{it.name || "—"}</td>
                          <td className="px-2 py-1.5 text-center text-slate-300">{qty || "—"}</td>
                          <td className="px-2 py-1.5 text-right">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={it.supplier_cost ?? ""}
                              onChange={(e) => onSupplierCostChange(idx, e.target.value)}
                              placeholder="—"
                              className="bg-slate-900 text-slate-100 border-slate-700 h-7 text-right text-xs"
                              data-testid={`pnl-supplier-cost-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right font-bold text-emerald-300">${(unit * qty).toFixed(2)}</td>
                          <td className={`px-2 py-1.5 text-right font-bold ${
                            lineMargin === null ? "text-slate-500"
                              : lineMargin >= 0 ? "text-amber-300" : "text-rose-400"
                          }`}>
                            {lineMargin === null ? "—" : `$${lineMargin.toFixed(2)}`}
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-300 text-xs">
                            {linePct === null ? "—" : `${linePct.toFixed(1)}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary card */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-right text-xs">
                <div className="bg-slate-800 border border-slate-700 rounded p-2">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Quote total</div>
                  <div className="text-base font-extrabold text-emerald-300" data-testid="pnl-quote-total">${totals.total.toFixed(2)}</div>
                </div>
                <div className="bg-slate-800 border border-slate-700 rounded p-2">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Supplier cost</div>
                  <div className="text-base font-extrabold text-rose-300" data-testid="pnl-supplier-cost">${totals.supplier_cost_total.toFixed(2)}</div>
                </div>
                <div className="bg-slate-800 border border-slate-700 rounded p-2">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Gross margin</div>
                  <div className="text-base font-extrabold text-amber-300" data-testid="pnl-gross-margin">
                    ${totals.gross_margin.toFixed(2)}
                    <span className="text-[10px] text-slate-400 ml-1">({totals.margin_pct.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="bg-slate-800 border border-slate-700 rounded p-2">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Commission</div>
                  <div className="text-base font-extrabold text-sky-300" data-testid="pnl-commission">${totals.commission.toFixed(2)}</div>
                </div>
                <div className="col-span-2 sm:col-span-1 bg-amber-900/40 border-2 border-amber-500 rounded p-2">
                  <div className="text-[10px] uppercase font-bold text-amber-300">Net to Sterile Mate</div>
                  <div className="text-lg font-extrabold text-amber-200" data-testid="pnl-net-to-company">${totals.net_to_company.toFixed(2)}</div>
                </div>
              </div>

              {/* Warning if any lines are missing supplier cost */}
              {lineItems.some((it) => !(parseFloat(it.supplier_cost) > 0)) && (
                <div className="text-[11px] text-amber-300 bg-amber-900/30 border border-amber-700 rounded px-2 py-1.5 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Some lines have no supplier cost entered — margins for those lines aren't included.
                  Use "Apply markup" to auto-fill from your supplier quotes, or type costs directly.
                </div>
              )}
            </>
          )}
        </section>
      )}
      {/* Non-financial-view roles get a passive notice that they're in the
          customer-facing draft — keeps them clear they're not the audit trail. */}
      {!canSeeFinancials && (
        <p className="text-[11px] text-slate-500 italic flex items-center gap-1.5" data-testid="pnl-locked-notice">
          <Lock className="w-3 h-3" />
          Customer-facing quote view. Cost &amp; margin data is restricted to super_admin / admin_accountant roles.
        </p>
      )}

      {/* Actions */}
      <div className="border-t border-slate-200 pt-3 pb-2 flex flex-wrap items-center justify-end gap-2 pr-20">
        <Button variant="outline" size="sm" onClick={previewPdf} className="gap-1.5" data-testid="quote-preview-btn">
          <Eye className="w-3.5 h-3.5" /> Preview PDF
        </Button>
        <Button variant="outline" size="sm" onClick={downloadPdf} className="gap-1.5" data-testid="quote-download-btn">
          <Download className="w-3.5 h-3.5" /> Download
        </Button>
        <Button size="sm" onClick={save} disabled={saving} className="gap-1.5 bg-slate-700 hover:bg-slate-800" data-testid="quote-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save draft
        </Button>
        <Button
          size="sm"
          onClick={() => setShowSend(true)}
          disabled={!toEmail || lineItems.length === 0}
          className="gap-1.5 bg-amber-600 hover:bg-amber-700"
          data-testid="quote-send-btn"
        >
          <Mail className="w-3.5 h-3.5" /> Email customer…
        </Button>
      </div>

      {/* Send modal */}
      {showSend && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !sending && setShowSend(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" /> Email quote to customer
            </h3>
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs">
                <div><strong>From:</strong> orders@sterilemate.com</div>
                <div><strong>Subject:</strong> Sterile Mate Quote {quoteNumber} — {backorder.hospital_name}</div>
                <div className="mt-1 text-slate-600"><strong>Total:</strong> ${totals.total.toFixed(2)} ({lineItems.length} line{lineItems.length === 1 ? "" : "s"})</div>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">To</label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-[10px] font-bold text-amber-800 hover:bg-amber-50"
                    onClick={() => setCrmPicker("to")}
                    data-testid="quote-modal-crm-to-btn"
                  >
                    <Users className="w-3 h-3 mr-1" /> Pick from CRM
                  </Button>
                </div>
                <Input
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="buyer@hospital.org"
                  className="h-9 text-sm"
                  data-testid="quote-to-input"
                />
                <label className="flex items-center gap-2 text-[11px] text-slate-600 mt-1.5">
                  <input
                    type="checkbox"
                    checked={savePrimary}
                    onChange={(e) => setSavePrimary(e.target.checked)}
                    className="accent-amber-600"
                    data-testid="quote-save-primary-contact"
                  />
                  Also save as primary contact on this order
                </label>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">CC (comma-separated, optional)</label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-[10px] font-bold text-slate-600 hover:bg-slate-100"
                    onClick={() => setCrmPicker("cc")}
                    data-testid="quote-modal-crm-cc-btn"
                  >
                    <Users className="w-3 h-3 mr-1" /> Pick from CRM
                  </Button>
                </div>
                <Input value={ccList} onChange={(e) => setCcList(e.target.value)} placeholder="rep@sterilemate.com, finance@hospital.org" className="h-9 text-sm" data-testid="quote-cc-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Opening note (optional, prepended to email body)</label>
                <Textarea rows={3} value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} placeholder="Per our conversation, please find our pricing for the requested items below…" className="text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} className="accent-amber-600" data-testid="quote-attach-pdf" />
                Attach branded PDF
              </label>
              <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                <AlertCircle className="w-3 h-3 inline mr-1 text-amber-700" />
                If the Resend domain isn't verified yet, the send will fall through to an Outlook draft (mailto) so the email still goes out.
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" size="sm" onClick={() => setShowSend(false)} disabled={sending}>Cancel</Button>
              <Button size="sm" onClick={send} disabled={sending || !(toEmail || "").trim()} className="gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="quote-send-confirm-btn">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Send quote
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* QuickBooks estimate review — nothing touches the draft until Apply */}
      {estProposal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEstProposal(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="quote-estimate-review">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
                QuickBooks estimate #{estProposal.estimate_number || "—"}
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {estProposal.customer_name || "—"}
                {estProposal.estimate_date ? ` · dated ${estProposal.estimate_date}` : ""}
                {` · subtotal $${(estProposal.subtotal || 0).toFixed(2)} · total $${(estProposal.total || 0).toFixed(2)}`}
              </p>
            </div>

            <div className="p-5 space-y-3 overflow-y-auto">
              {!!(estProposal.confidence_notes || []).length && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  {estProposal.confidence_notes.join(" · ")}
                </div>
              )}
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {(estProposal.line_items || []).map((li, i) => (
                  <label key={i} className="flex items-start gap-2.5 p-2.5 text-sm hover:bg-slate-50 cursor-pointer" data-testid={`quote-estimate-line-${i}`}>
                    <input
                      type="checkbox"
                      checked={!!estPicked[i]}
                      onChange={(e) => setEstPicked((p) => p.map((v, j) => (j === i ? e.target.checked : v)))}
                      className="accent-emerald-700 mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                        <span className="truncate">{li.name || "(no name)"}</span>
                        {li.is_freight && (
                          <span className="text-[9px] font-black uppercase tracking-wider text-cyan-800 bg-cyan-50 border border-cyan-200 rounded px-1 py-0.5">freight</span>
                        )}
                      </div>
                      {li.notes && (
                        <div className="text-[11px] text-slate-500 line-clamp-2 whitespace-pre-wrap">{li.notes}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0 text-xs">
                      <div className="text-slate-500">{li.quantity} {li.uom} × ${Number(li.unit_price || 0).toFixed(2)}</div>
                      <div className="font-bold text-slate-900">${Number(li.extended_price || 0).toFixed(2)}</div>
                    </div>
                  </label>
                ))}
              </div>

              {(estProposal.freight_total || 0) > 0 && (
                <label className="flex items-center gap-2 text-sm bg-cyan-50 border border-cyan-200 rounded p-2.5">
                  <input
                    type="checkbox"
                    checked={estFreightToOverride}
                    onChange={(e) => setEstFreightToOverride(e.target.checked)}
                    className="accent-cyan-700"
                    data-testid="quote-estimate-freight-toggle"
                  />
                  <span className="text-cyan-900">
                    Move the freight / fuel-surcharge lines (${(estProposal.freight_total || 0).toFixed(2)}) into the quote's <strong>Freight&nbsp;$ override</strong> instead of the line table
                  </span>
                </label>
              )}

              <div className="text-[11px] text-slate-500">
                Terms <strong>{estProposal.terms || "Net 30"}</strong>
                {estProposal.expiration_date ? ` · valid until ${estProposal.expiration_date}` : ""}
                {` — applied with the lines. Supplier $ stays blank so you can key your cost for margin.`}
              </div>
            </div>

            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl flex flex-wrap items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEstProposal(null)} data-testid="quote-estimate-cancel">Cancel</Button>
              <Button variant="outline" size="sm" onClick={() => applyEstimate("append")} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="quote-estimate-append">
                <Plus className="w-3.5 h-3.5 mr-1" /> Append to existing lines
              </Button>
              <Button size="sm" onClick={() => applyEstimate("replace")} className="bg-emerald-700 hover:bg-emerald-800" data-testid="quote-estimate-replace">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Replace all lines
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CRM address book — change the quote recipient (or add CC) without
          typing an address from memory; also allows quick-adding a contact. */}
      <CrmContactPicker
        open={!!crmPicker}
        onOpenChange={(o) => !o && setCrmPicker(null)}
        mode={crmPicker === "cc" ? "multi" : "single"}
        title={crmPicker === "cc" ? "Add CC recipients from CRM" : "Change quote recipient"}
        confirmLabel={crmPicker === "cc" ? "Add to CC" : "Use as recipient"}
        onConfirm={applyCrmPick}
        hospitalId={backorder.hospital_id || ""}
        hospitalName={backorder.hospital_name || ""}
      />
    </div>
  );
}
